import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { Paginated, Quote, QuoteStatus } from "../types/dtos";

export type QuoteListParams = { page?: number; pageSize?: number; q?: string; status?: string };

async function fetchQuotes(params: QuoteListParams): Promise<Paginated<Quote>> {
  const res = await staffApi.get("/quotes", { params });
  return res.data;
}

export function useQuotes(params: QuoteListParams): UseQueryResult<Paginated<Quote>> {
  return useQuery({
    queryKey: ["quotes", params],
    queryFn: () => fetchQuotes(params),
    placeholderData: (prev: Paginated<Quote> | undefined) => prev,
  });
}

export type QuoteItemFormInput = { description: string; skillLevelId: number; hours: number };

export type QuoteFormInput = {
  customerId?: number;
  leadName?: string;
  leadContact?: string;
  notes?: string;
  items: QuoteItemFormInput[];
};

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuoteFormInput) => staffApi.post("/quotes", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

export function useUpdateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: QuoteFormInput }) => staffApi.patch(`/quotes/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

export function useTransitionQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: QuoteStatus }) => staffApi.post(`/quotes/${id}/transition`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => staffApi.delete(`/quotes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

export async function openQuotePdf(id: number): Promise<void> {
  const res = await staffApi.get(`/quotes/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
